import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import type { CalendarEvent } from "@tellescope/types-models";
import { useLocalTracks, useMeetingOperations } from "@tellescope/video-calling";
import { useEnduserStore, useVideoCalling } from "../store";

interface UseJoinVideoCallReturn {
  joinVideoCall: (event: CalendarEvent) => void;
  connectToCall: (calendarEventId: string) => Promise<void>;
}

// The SDK rethrows the API's error body (a plain `{ message }` object) rather than an Error
const getErrorMessage = (error: unknown): string | undefined => {
  if (typeof error === "object" && error !== null && "message" in error && typeof error.message === "string") {
    return error.message;
  }
  return undefined;
};

// Read the live store, not render-time state: these checks run after awaits, when the enduser may have closed the
// call. Releasing media then keeps the camera/mic from staying live (or published to the room) with no UI.
const releaseIfClosed = () => {
  const { videoCalling } = useEnduserStore.getState();
  if (videoCalling.isVisible) return false;
  videoCalling.resetVideoState();
  return true;
};

/**
 * Hook for joining Twilio/Chime video calls from calendar events.
 *
 * `joinVideoCall` opens the waiting room (with a camera preview) instead of connecting directly. The waiting
 * room confirms with the server that the provider has started the meeting and then calls `connectToCall`,
 * so an enduser can never join before the provider.
 *
 * @example
 * const { joinVideoCall } = useJoinVideoCall();
 * <Button onClick={() => joinVideoCall(event)}>Join</Button>
 */
export const useJoinVideoCall = (): UseJoinVideoCallReturn => {
  const { t } = useTranslation("videoCalling");
  const videoCalling = useVideoCalling();
  const { createLocalTracks } = useLocalTracks();
  const { joinMeeting } = useMeetingOperations();

  const joinVideoCall = useCallback(
    (event: CalendarEvent) => {
      const { isVisible, isWaitingRoom } = useEnduserStore.getState().videoCalling;
      // A second click while the waiting room is open would start a second, leaked set of tracks
      if (isVisible && isWaitingRoom) return;

      videoCalling.setConnectionError(null);
      videoCalling.setMeetingMeta({
        calendarEventId: event.id,
        eventTitle: event.title,
        isHost: false,
      });
      videoCalling.setIsWaitingRoom(true);
      videoCalling.setIsVisible(true);

      // Failures are stored as deviceError, which the waiting room shows with a retry
      createLocalTracks().then(releaseIfClosed, () => undefined);
    },
    [videoCalling, createLocalTracks]
  );

  const connectToCall = useCallback(
    async (calendarEventId: string) => {
      try {
        await joinMeeting(calendarEventId);
        releaseIfClosed();
      } catch (err) {
        if (releaseIfClosed()) return;
        videoCalling.setConnectionError(getErrorMessage(err) ?? t("Failed to join meeting"));
      }
    },
    [videoCalling, joinMeeting, t]
  );

  return {
    joinVideoCall,
    connectToCall,
  };
};
